import React from 'react';
import PropTypes from 'prop-types';
import { bool, func, shape } from 'prop-types';
import { connect } from 'react-redux';

import { TextInput } from 'jl-design-system/components/text-input/TextInput';
import Checkbox from 'jl-design-system/components/checkbox/Checkbox';
import { Select } from 'jl-design-system/components/select/Select';
import DateInput from 'jl-design-system/components/date-input/DateInput';
import OptionList from 'jl-design-system/components/option/OptionList';

import { errorMessagesActions } from '../../messages';
import { OCAB_QUESTION_TYPE } from '~/components/questions/helpers';

import styles from './FormFieldAdapter.scss';

export class FormFieldAdapter extends React.Component {
  state = {
    touched: false,
    infoBoxMessage: '',
  };

  componentDidMount() {
    const { value } = this.props;
    this.handleShowInfoBox(value);
  }

  handleShowInfoBox(value) {
    const { type, options } = this.props.formFieldConfig;
    if (type !== 'RADIO') return;

    let selectedValue = value;
    if (Array.isArray(value)) selectedValue = value[0];
    else if (typeof value === 'object') selectedValue = value.target?.value;

    const option = options?.find((option) => option.name === selectedValue);
    if (option?.message) this.setState({ infoBoxMessage: option.message });
    else this.setState({ infoBoxMessage: '' });
  }

  isOptionsLimitReached(value) {
    const { type, optionsLimit } = this.props.formFieldConfig;
    if (type === 'CHECKBOXES' && optionsLimit && value.length > optionsLimit) return true;
  }

  handleChange = (eventOrValue) => {
    if (this.isOptionsLimitReached(eventOrValue)) return;

    const { onChange, input, dispatchResetErrorMessagesWith } = this.props;

    this.handleShowInfoBox(eventOrValue);

    dispatchResetErrorMessagesWith([]);

    // Extract onChange from input prop if passed, otherwise use onChange prop directly
    const changeHandler = input?.onChange || onChange;
    if (changeHandler) {
      changeHandler(eventOrValue);
    }
  };

  handleBlur = () => {
    this.setState({ touched: true });
    const { onBlur, input } = this.props;

    // Extract onBlur from input prop if passed, otherwise use onBlur prop directly
    const blurHandler = input?.onBlur || onBlur;
    if (blurHandler) {
      blurHandler();
    }
  };

  removeWhiteSpace = (str) => str.replace(/ /g, '');

  renderField() {
    const { formFieldConfig, value, name, error, input, meta } = this.props;
    const { touched } = this.state;
    const {
      component: Component,
      type,
      options,
      supportingText,
      description,
      instruction,
      ...fieldConfig
    } = formFieldConfig;

    const suppressComponentErrorMessage =
      type === OCAB_QUESTION_TYPE.TEXTAREA ||
      type === OCAB_QUESTION_TYPE.TEXT_FIELD ||
      type === OCAB_QUESTION_TYPE.RADIO_PRICE;
    const fieldSupportingText = supportingText || description || instruction || undefined;

    // Extract name, value from input prop if passed, otherwise use direct props
    const fieldName = input?.name || name;
    const fieldValue = input?.value !== undefined ? input.value : value;
    const fieldError = meta?.error || error;
    const fieldTouched = meta?.touched !== undefined ? meta.touched : touched;
    const fieldSubmitFailed = meta?.submitFailed || false;
    // Show error if field has been touched OR if form submission failed with errors
    const showError = (fieldTouched || fieldSubmitFailed) && fieldError;

    const commonProps = {
      id: fieldName,
      name: fieldName,
      value: fieldValue || '',
      labelText: fieldConfig.label || fieldConfig.labelText,

      ...fieldConfig,
      error: suppressComponentErrorMessage ? undefined : showError ? fieldError : undefined,
      errorText: suppressComponentErrorMessage ? undefined : showError ? fieldError : undefined,
      supportingText: fieldSupportingText,
      onBlur: this.handleBlur,
    };

    if (Component) {
      // For checkbox components, use 'checked' instead of 'value'
      const isCheckboxType = type === 'checkbox' || type === OCAB_QUESTION_TYPE.CHECKBOX;
      const checkboxProps = isCheckboxType ? { checked: !!fieldValue } : {};

      return (
        <Component
          {...commonProps}
          {...checkboxProps}
          type={type}
          options={options}
          onChange={(e) => {
            const data = e;
            if (fieldName?.includes('phoneNumber') && e.target) {
              data.target.value = this.removeWhiteSpace(e.target.value);
            }
            this.handleChange(data);
          }}
        />
      );
    }

    // Default rendering based on type
    if (type === 'TEXT' || type === 'EMAIL' || type === 'TEL') {
      return (
        <TextInput
          {...commonProps}
          type={type.toLowerCase()}
          onChange={(e) => {
            const data = e;
            if (fieldName?.includes('phoneNumber')) {
              data.target.value = this.removeWhiteSpace(e.target.value);
            }
            this.handleChange(data);
          }}
          sessionCamExclude
        />
      );
    }

    if ([OCAB_QUESTION_TYPE.CHECKBOX, OCAB_QUESTION_TYPE.DROPDOWN_CHECKBOX].includes(type)) {
      return <Checkbox {...commonProps} checked={!!fieldValue} onChange={this.handleChange} />;
    }

    if (type === 'CHECKBOXES' || type === 'RADIO') {
      return <OptionList {...commonProps} options={options || []} onChange={this.handleChange} />;
    }

    if (type === 'SELECT') {
      return <Select {...commonProps} options={options || []} onChange={this.handleChange} />;
    }

    if (type === 'DATE') {
      return <DateInput {...commonProps} onChange={this.handleChange} />;
    }

    return null;
  }

  render() {
    const { infoBoxMessage } = this.state;
    return (
      <>
        {this.renderField()}
        {!!infoBoxMessage && (
          <div className={styles.InfoBox} dangerouslySetInnerHTML={{ __html: infoBoxMessage }} />
        )}
      </>
    );
  }
}

FormFieldAdapter.propTypes = {
  formFieldConfig: shape({
    component: func,
    type: PropTypes.string,
    required: bool,
  }).isRequired,

  name: PropTypes.string,
  value: PropTypes.any,
  error: PropTypes.string,
  onChange: func,
  onBlur: func,
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onChange: func,
    onBlur: func,
  }),
  meta: PropTypes.shape({
    error: PropTypes.string,
    touched: PropTypes.bool,
  }),

  dispatchResetErrorMessagesWith: func.isRequired,
};

const mapDispatchToProps = {
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
};

export const ConnectedFormFieldAdapter = connect(undefined, mapDispatchToProps)(FormFieldAdapter);
